import api from './api-backend';
import type {
  DashboardReportResponse,
  DashboardReport,
  GetDashboardReportRequest,
  GetReportRequest,
  RoomAvailabilityResponse,
  ReservationAnalyticsResponse,
  BookingChannelsResponse,
  AIChatRequest,
  AIChatAPIResponse,
  ReportExportJobStreamData,
  ReportExportStreamEvent,
  QuickInsightRequest,
  QuickInsightAPIResponse,
  CreateReportExportRequest,
  ReportExportJobResponse,
  ReportExportJobStatusResponse,
  GetReportExportJobsParams,
  GetReportExportJobsResponse,
  ReportExportDownloadResponse,
  ReportExportAPIResponse,
  GetReservationsReportRequest,
  ReservationsReportResponse,
  GetExportJobsRequest,
  ExportJobListResponse,
  CreateExportJobRequest,
  MarketSegmentAnalysisResponse,
  BookingChannelAnalysisResponse,
  RoomTypeAnalysisResponse,
  BookingWindowAnalysisResponse,
  NationalityAnalysisResponse,
  ExportJobListItem,
  GetBookingPerformanceRequest,
  BookingPerformanceResponse,
  RoomNightsByMonthResponse,
  NationalityDistributionResponse,
  GetRevenueMovementYearRequest,
  GetRevenueMovementMonthRequest,
  GetRevenueMovementDayRequest,
  YearSummaryResponse,
  MonthDailyResponse,
  DayBreakdownResponse,
} from 'types/report';

/**
 * Report Service
 * Service for handling report API calls
 */
export class ReportService {
  /**
   * Get dashboard report data based on date range and property ID
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing dashboard report data
   */
  static async getDashboardReport(params: GetDashboardReportRequest): Promise<DashboardReportResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params.compare_period !== undefined) queryParams.append('compare_period', params.compare_period.toString());

    const queryString = queryParams.toString();

    const [summaryRes, segmentsRes, channelsRes, analyticsRes] = await Promise.all([
        api.get(`/v1/dashboard/summary?${queryString}`),
        api.get(`/v1/dashboard/market-segments?${queryString}`),
        api.get(`/v1/dashboard/booking-channels?${queryString}`),
        api.get(`/v1/dashboard/reservation-analytics?${queryString}`),
      ]);

      const summary = summaryRes.data.data; // Assuming standard response wrapper
      const segments = segmentsRes.data.data;
      const channels = channelsRes.data.data;
      const analytics = analyticsRes.data.data;

      // Map to DashboardReport structure
      const reportData: DashboardReport = {
        property_id: params.property_id,
        start_date: params.start_date,
        end_date: params.end_date,
        last_updated: new Date().toISOString(),
        performance: {
          occupancy: summary.kpis.occupancy,
          adr: summary.kpis.adr,
          revpar: summary.kpis.revpar,
          total_revenue: summary.kpis.revenue,
        },
        market_segments: segments || [],
        booking_channels: channels || [],
        time_series: analytics.daily_analytics.map((day: Record<string, unknown>) => ({
          date: day.date,
          revenue: day.total_revenue,
          occupancy: 0, // Not available in reservation analytics directly, would need room availability
          bookings: day.total_reservations,
        })),
      };

      return {
        meta: {
          status: 'success',
          message: 'Dashboard report retrieved successfully',
          timestamp: new Date().toISOString(),
        },
        data: reportData,
      };
  }

  /**
   * Get room availability data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing room availability data
   */
  static async getRoomAvailability(params: GetReportRequest): Promise<RoomAvailabilityResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<RoomAvailabilityResponse>(
      `/v1/dashboard/room-availability?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get reservation analytics data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing reservation analytics data
   */
  static async getReservationAnalytics(params: GetReportRequest): Promise<ReservationAnalyticsResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.check_in_start) queryParams.append('check_in_start', params.check_in_start);
    if (params.check_in_end) queryParams.append('check_in_end', params.check_in_end);
    if (params.check_out_start) queryParams.append('check_out_start', params.check_out_start);
    if (params.check_out_end) queryParams.append('check_out_end', params.check_out_end);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<ReservationAnalyticsResponse>(
      `/v1/dashboard/reservation-analytics?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get booking channels data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing booking channels data
   */
  static async getBookingChannels(params: GetReportRequest): Promise<BookingChannelsResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<BookingChannelsResponse>(`/v1/dashboard/booking-channels?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get dashboard report data with custom query parameters (legacy method)
   * @param propertyId - Property ID to fetch report for
   * @param startDate - Start date for the report (YYYY-MM-DD format)
   * @param endDate - End date for the report (YYYY-MM-DD format)
   * @returns Promise containing dashboard report data
   */
  static async getDashboardReportByParams(
    propertyId: string,
    startDate: string,
    endDate: string
  ): Promise<DashboardReportResponse> {
    return this.getDashboardReport({
      property_id: propertyId,
      start_date: startDate,
      end_date: endDate,
    });
  }

  /**
   * Chat with AI assistant about report data
   * @param request - AI chat request with message and context
   * @returns Promise containing AI chat response
   */
  static async chatWithAI(request: AIChatRequest): Promise<AIChatAPIResponse> {
    const response = await api.post<AIChatAPIResponse>('/v1/report/ai-chat', request);
    return response.data;
  }

  /**
   * Generate quick AI insights for current report data
   * @param request - Quick insight request with focus areas
   * @returns Promise containing quick insights response
   */
  static async generateQuickInsights(request: QuickInsightRequest): Promise<QuickInsightAPIResponse> {
    const response = await api.post<QuickInsightAPIResponse>('/v1/report/ai-insights', request);
    return response.data;
  }

  /**
   * Chat with AI using simple message (convenience method)
   * @param message - User message/question
   * @param propertyId - Optional property ID for context
   * @param startDate - Optional start date for report context
   * @param endDate - Optional end date for report context
   * @returns Promise containing AI chat response
   */
  static async askAI(
    message: string,
    propertyId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<AIChatAPIResponse> {
    return this.chatWithAI({
      message,
      property_id: propertyId,
      start_date: startDate,
      end_date: endDate,
      include_report_data: true,
    });
  }

  /**
   * Generate insights for specific focus areas (convenience method)
   * @param focusAreas - Areas to focus insights on (e.g., ['occupancy', 'revenue'])
   * @param propertyId - Optional property ID for context
   * @param startDate - Optional start date for analysis
   * @param endDate - Optional end date for analysis
   * @returns Promise containing quick insights response
   */
  static async getInsights(
    focusAreas: string[] = ['occupancy', 'revenue', 'adr', 'revpar'],
    propertyId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<QuickInsightAPIResponse> {
    return this.generateQuickInsights({
      focus_areas: focusAreas,
      property_id: propertyId,
      start_date: startDate,
      end_date: endDate,
      max_insights: 5,
    });
  }

  /**
   * Create a new report export job
   * @param request - Export request containing report type, format, and filters
   * @returns Promise containing export job response
   */
  static async createReportExport(
    request: CreateReportExportRequest
  ): Promise<ReportExportAPIResponse<ReportExportJobResponse>> {
    const response = await api.post<ReportExportAPIResponse<ReportExportJobResponse>>('/v1/report/export', request);
    return response.data;
  }

  /**
   * Get list of report export jobs with pagination and filters
   * @param params - Query parameters for filtering and pagination
   * @returns Promise containing paginated list of export jobs
   */
  static async getReportExportJobs(params?: GetReportExportJobsParams): Promise<GetReportExportJobsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.report_type) {
      queryParams.append('report_type', params.report_type);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const response = await api.get<GetReportExportJobsResponse>(`/v1/report/export/jobs?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get status and details of a specific export job
   * @param jobId - Export job ID to retrieve status for
   * @returns Promise containing export job status
   */
  static async getReportExportJobStatus(
    jobId: string
  ): Promise<ReportExportAPIResponse<ReportExportJobStatusResponse>> {
    const response = await api.get<ReportExportAPIResponse<ReportExportJobStatusResponse>>(
      `/v1/report/export/${jobId}`
    );
    return response.data;
  }

  /**
   * Get download URL for a completed export job
   * @param jobId - Export job ID to download
   * @returns Promise containing download URL and file information
   */
  static async downloadReportExportFile(jobId: string): Promise<ReportExportAPIResponse<ReportExportDownloadResponse>> {
    const response = await api.get<ReportExportAPIResponse<ReportExportDownloadResponse>>(
      `/v1/report/export/${jobId}/download`
    );
    return response.data;
  }

  /**
   * Delete an export job and its associated file
   * @param jobId - Export job ID to delete
   * @returns Promise containing delete confirmation
   */
  static async deleteReportExportJob(jobId: string): Promise<ReportExportAPIResponse<{ id: string }>> {
    const response = await api.delete<ReportExportAPIResponse<{ id: string }>>(`/v1/report/export/${jobId}`);
    return response.data;
  }

  /**
   * Stream report export job updates via SSE
   * Endpoint: GET /v1/report/export/stream?job_id={jobId}
   * @param jobId - Export job ID to stream updates for
   * @param onUpdate - Callback function called when job data updates
   * @param onError - Optional callback for error handling
   * @param autoClose - Auto-close stream when job completes (default: true)
   * @returns Unsubscribe function to close the stream
   */
  static streamReportExportUpdates(
    jobId: string,
    onUpdate: (data: ReportExportJobStreamData) => void,
    onError?: (event: Event) => void,
    autoClose = true
  ): () => void {
    const baseURL = api.defaults.baseURL || '';
    const token = localStorage.getItem('auth_token');
    const streamUrl = `${baseURL}/v1/report/export/stream?job_id=${jobId}${token ? `&token=${token}` : ''}`;

    const eventSource = new EventSource(streamUrl);

    eventSource.onmessage = event => {
      try {
        const response = JSON.parse(event.data) as ReportExportStreamEvent;

        let data: ReportExportJobStreamData;
        if (response.type === 'initial' && response.data) {
          data = response.data;
        } else if (response.type === 'update' && response.data) {
          data = response.data;
        } else {
          data = response as unknown as ReportExportJobStreamData;
        }

        onUpdate(data);

        if (autoClose && (data.status === 'completed' || data.status === 'failed')) {
          eventSource.close();
        }
      } catch {
        if (onError) {
          onError(event);
        }
      }
    };

    eventSource.onerror = event => {
      if (onError) {
        onError(event);
      }
    };

    return () => {
      eventSource.close();
    };
  }

  /**
   * Get reservations report with filtering, sorting, and pagination
   * @param params - Request parameters for filtering and pagination
   * @returns Promise containing reservations report data
   */

  static async getReservationsReport(params: GetReservationsReportRequest): Promise<ReservationsReportResponse> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<ReservationsReportResponse>(`/v1/report/reservations?${queryParams.toString()}`);
    return response.data;
  }

  static async exportReservationsReport(params: GetReservationsReportRequest, format: 'csv' | 'excel'): Promise<void> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    queryParams.append('format', format);

    const response = await api.get(`/v1/report/reservations/export?${queryParams.toString()}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const extension = format === 'csv' ? 'csv' : 'xlsx';
    link.setAttribute('download', `reservations_report.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  static async getExportJobs(params: GetExportJobsRequest): Promise<ExportJobListResponse> {
    const queryParams = new URLSearchParams();

    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.report_type) queryParams.append('report_type', params.report_type);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get<{
      data: { jobs: ExportJobListItem[] };
      pagination: { total_items: number; current_page: number; per_page: number; total_pages: number };
    }>(`/v1/report/export/jobs?${queryParams.toString()}`);

    return {
      jobs: response.data.data.jobs,
      total: response.data.pagination.total_items,
      page: response.data.pagination.current_page,
      limit: response.data.pagination.per_page,
      total_pages: response.data.pagination.total_pages,
    };
  }

  static async createExportJob(data: CreateExportJobRequest): Promise<void> {
    await api.post('/v1/report/export', data);
  }

  static async downloadExportFile(jobId: string, fileName: string): Promise<void> {
    const response = await api.get<ReportExportAPIResponse<ReportExportDownloadResponse>>(
      `/v1/report/export/${jobId}/download`
    );

    const { download_url, file_name } = response.data.data;
    const link = document.createElement('a');
    link.href = download_url;
    link.setAttribute('download', file_name || fileName);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  static async deleteExportJob(jobId: string): Promise<void> {
    await api.delete(`/v1/report/export/${jobId}`);
  }

  /**
   * Get market segment analysis data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing market segment analysis data
   */
  static async getMarketSegmentAnalysis(params: GetReportRequest): Promise<MarketSegmentAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<MarketSegmentAnalysisResponse>(
      `/v1/report/market-segment-analysis?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get booking channel analysis data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing booking channel analysis data
   */
  static async getBookingChannelAnalysis(
    params: GetReportRequest & { page?: number; limit?: number }
  ): Promise<BookingChannelAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params.source) queryParams.append('source', params.source);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get<BookingChannelAnalysisResponse>(
      `/v1/report/booking-channel-analysis?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get room type analysis data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing room type analysis data
   */
  static async getRoomTypeAnalysis(params: GetReportRequest): Promise<RoomTypeAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<RoomTypeAnalysisResponse>(`/v1/report/room-type-analysis?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get booking window analysis data
   * @param params - Request parameters containing property_id, start_date, and end_date
   * @returns Promise containing booking window analysis data
   */
  static async getBookingWindowAnalysis(params: GetReportRequest): Promise<BookingWindowAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<BookingWindowAnalysisResponse>(
      `/v1/report/booking-window-analysis?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get nationality analysis report
   * @param params - Request parameters containing property_id, organization_id, start_date, and end_date
   * @returns Promise containing nationality analysis data
   */
  static async getNationalityAnalysis(params: GetReportRequest): Promise<NationalityAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<NationalityAnalysisResponse>(
      `/v1/report/nationality-analysis?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Get booking performance data with metric and period filters
   * @param params - Request parameters containing property_id, organization_id, metric, and period
   * @returns Promise containing booking performance data
   */
  static async getBookingPerformance(params: GetBookingPerformanceRequest): Promise<BookingPerformanceResponse> {
    const queryParams = new URLSearchParams();
    if (params.property_id) queryParams.append('property_id', params.property_id);
    queryParams.append('organization_id', params.organization_id);
    queryParams.append('metric', params.metric);
    queryParams.append('period', params.period);

    const response = await api.get<{ data: BookingPerformanceResponse }>(
      `/v1/report/booking-performance?${queryParams.toString()}`
    );
    return response.data.data;
  }

  /**
   * Get room nights booked by month of stay
   * @param propertyId - Optional property ID
   * @param organizationId - Optional organization ID
   * @returns Promise containing room nights by month data
   */
  static async getRoomNightsByMonth(propertyId?: string, organizationId?: string): Promise<RoomNightsByMonthResponse> {
    const queryParams = new URLSearchParams();
    if (propertyId) queryParams.append('property_id', propertyId);
    if (organizationId) queryParams.append('organization_id', organizationId);

    const response = await api.get<{ data: RoomNightsByMonthResponse }>(
      `/v1/report/room-nights-by-month?${queryParams.toString()}`
    );
    return response.data.data;
  }

  /**
   * Get nationality distribution of bookings
   * @param propertyId - Optional property ID
   * @param organizationId - Optional organization ID
   * @returns Promise containing nationality distribution data
   */
  static async getNationalityDistribution(
    propertyId?: string,
    organizationId?: string
  ): Promise<NationalityDistributionResponse> {
    const queryParams = new URLSearchParams();
    if (propertyId) queryParams.append('property_id', propertyId);
    if (organizationId) queryParams.append('organization_id', organizationId);

    const response = await api.get<{ data: NationalityDistributionResponse }>(
      `/v1/report/nationality-distribution?${queryParams.toString()}`
    );
    return response.data.data;
  }

  static async getRevenueMovementYear(
    params: GetRevenueMovementYearRequest
  ): Promise<{ data: YearSummaryResponse }> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', params.year.toString());
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.compare_year) queryParams.append('compare_year', params.compare_year.toString());
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<{ data: YearSummaryResponse }>(
      `/v1/report/revenue-movement/year?${queryParams.toString()}`
    );
    return response.data;
  }

  static async getRevenueMovementMonth(
    params: GetRevenueMovementMonthRequest
  ): Promise<{ data: MonthDailyResponse }> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', params.year.toString());
    queryParams.append('month', params.month.toString());
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<{ data: MonthDailyResponse }>(
      `/v1/report/revenue-movement/month?${queryParams.toString()}`
    );
    return response.data;
  }

  static async getRevenueMovementDay(
    params: GetRevenueMovementDayRequest
  ): Promise<{ data: DayBreakdownResponse }> {
    const queryParams = new URLSearchParams();
    queryParams.append('date', params.date);
    if (params.property_id) queryParams.append('property_id', params.property_id);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const response = await api.get<{ data: DayBreakdownResponse }>(
      `/v1/report/revenue-movement/day?${queryParams.toString()}`
    );
    return response.data;
  }
}

export default ReportService;
